import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { motion } from "motion/react";
import { ArrowRight, CalendarPlus, GraduationCap, Sparkle, X } from "lucide-react";
import { cn } from "@/utils/helpers";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { MarketSignal } from "@/types/marketSignal.type";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import {
  HORIZON_STATE_COLORS,
  calendarDaysUntil,
  computeHorizon,
  daysLabel,
  formatShortDate,
  horizonState,
  kindLabelSingular,
  monthLabel,
  parseDate,
  type HorizonState,
} from "./signalMeta";
import { RUNWAY_TRANSITION } from "./signalsMotion";
import KindGlyph from "./KindGlyph";
import LifetimePicker from "./LifetimePicker";
import SignalEvidenceSources from "./SignalEvidenceSources";

const DAY = 24 * 60 * 60 * 1000;
/** The runway reads the last month and the next quarter, so today sits at 25%. */
const WINDOW_BACK_DAYS = 30;
const WINDOW_AHEAD_DAYS = 90;
const TODAY_X = (WINDOW_BACK_DAYS / (WINDOW_BACK_DAYS + WINDOW_AHEAD_DAYS)) * 100;

interface Scale {
  start: number;
  end: number;
  now: Date;
}

function makeScale(now: Date): Scale {
  return {
    start: now.getTime() - WINDOW_BACK_DAYS * DAY,
    end: now.getTime() + WINDOW_AHEAD_DAYS * DAY,
    now,
  };
}

/** Percent along the runway, unclamped. */
function xOf(scale: Scale, t: number): number {
  return ((t - scale.start) / (scale.end - scale.start)) * 100;
}

function clampPct(x: number): number {
  return Math.max(0, Math.min(100, x));
}

/** Every month that starts inside the runway window, for the axis. */
function monthTicks(scale: Scale): { x: number; label: string; key: string }[] {
  const ticks: { x: number; label: string; key: string }[] = [];
  const first = new Date(scale.start);
  // A window that opens on the 1st keeps that month's tick.
  let d =
    first.getDate() === 1 && first.getHours() === 0 && first.getMinutes() === 0
      ? new Date(first.getFullYear(), first.getMonth(), 1)
      : new Date(first.getFullYear(), first.getMonth() + 1, 1);
  while (d.getTime() <= scale.end) {
    const label =
      d.getMonth() === 0 ? `${monthLabel(d)} ${d.getFullYear()}` : monthLabel(d);
    ticks.push({ x: xOf(scale, d.getTime()), label, key: d.toISOString() });
    d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  }
  return ticks;
}

interface Runway {
  state: HorizonState;
  openEnded: boolean;
  expired: boolean;
  startX: number;
  /** null when open-ended. */
  endX: number | null;
  /** The end is past the right edge of the window and was clamped to it. */
  endClamped: boolean;
  /** Calendar days left; null when open-ended or already over. */
  days: number | null;
  startLabel: string;
  endLabel: string;
  aria: string;
}

function runwayOf(signal: MarketSignal, scale: Scale): Runway {
  const horizon = computeHorizon(signal, scale.now);
  const state = horizonState(horizon);
  const from = parseDate(signal.validFrom).getTime();
  const startX = clampPct(xOf(scale, from));
  const startLabel = formatShortDate(signal.validFrom, scale.now);

  if (!signal.validUntil) {
    return {
      state,
      openEnded: true,
      expired: false,
      startX,
      endX: null,
      endClamped: false,
      days: null,
      startLabel,
      endLabel: "Until superseded",
      aria: `${signal.title}: from ${startLabel}, until superseded`,
    };
  }

  const endRaw = xOf(scale, parseDate(signal.validUntil).getTime());
  const endLabelDate = formatShortDate(signal.validUntil, scale.now);
  const expired = horizon.expired;
  const days = expired ? null : Math.max(0, calendarDaysUntil(signal.validUntil, scale.now));
  const left =
    days == null ? "window ended" : days === 0 ? "ends today" : `${daysLabel(days)} left`;

  return {
    state,
    openEnded: false,
    expired,
    startX,
    endX: clampPct(endRaw),
    endClamped: endRaw > 100,
    days,
    startLabel,
    endLabel: `${expired ? "Ended" : "Ends"} ${endLabelDate}`,
    aria: `${signal.title}: from ${startLabel} to ${endLabelDate}, ${left}`,
  };
}

/** Days left ascending, open-ended last, ties by title. */
function sortKey(signal: MarketSignal, now: Date): number {
  if (!signal.validUntil) return Number.POSITIVE_INFINITY;
  return calendarDaysUntil(signal.validUntil, now);
}

/**
 * The runway: a 1px track, the ELAPSED stretch of the window up to today, the
 * REMAINING stretch from today to the end in the horizon state colour, and an
 * end cap. On first mount both stretches draw outward from the today point.
 */
function RunwayBar({ runway, drawIn }: { runway: Runway; drawIn: boolean }) {
  const { startX, endX, openEnded, expired } = runway;
  const color = HORIZON_STATE_COLORS[runway.state];
  const reach = openEnded ? 100 : (endX ?? 100);

  // Elapsed runs from the start to today (or to the end, once the window is over).
  const elapsedTo = Math.min(TODAY_X, reach);
  const hasElapsed = elapsedTo > startX;
  // Remaining runs from today (or a later start) to the end.
  const remainingFrom = Math.max(TODAY_X, startX);
  const hasRemaining = !expired && reach >= remainingFrom;
  const cap = !openEnded && !runway.endClamped;

  const initial = drawIn ? { scaleX: 0 } : false;
  const endAnchorEnd = openEnded || reach > 30;

  return (
    <div className="sig-run" role="img" aria-label={runway.aria}>
      <span className="sig-run__track" aria-hidden="true" />
      {hasElapsed ? (
        <motion.span
          className="sig-run__seg sig-run__seg--elapsed"
          aria-hidden="true"
          style={{ insetInlineStart: `${startX}%`, width: `${elapsedTo - startX}%`, originX: 1 }}
          initial={initial}
          animate={{ scaleX: 1 }}
          transition={RUNWAY_TRANSITION}
        >
          {expired && cap ? <span className="sig-run__cap" style={{ background: color }} /> : null}
        </motion.span>
      ) : null}
      {hasRemaining ? (
        <motion.span
          className="sig-run__seg"
          aria-hidden="true"
          style={{
            insetInlineStart: `${remainingFrom}%`,
            width: `${reach - remainingFrom}%`,
            background: color,
            originX: 0,
          }}
          initial={initial}
          animate={{ scaleX: 1 }}
          transition={RUNWAY_TRANSITION}
        >
          {cap ? <span className="sig-run__cap" style={{ background: color }} /> : null}
        </motion.span>
      ) : null}

      <span
        className={cn("sig-run__label sig-run__label--end", endAnchorEnd && "is-anchor-end")}
        style={{ insetInlineStart: `${reach}%` }}
        aria-hidden="true"
      >
        {runway.endLabel}
        {runway.endClamped ? <ArrowRight className="sig-run__more" aria-hidden="true" /> : null}
      </span>
      <span
        className={cn("sig-run__label sig-run__label--start", startX > 70 && "is-anchor-end")}
        style={{ insetInlineStart: `${startX}%` }}
        aria-hidden="true"
      >
        {runway.startLabel}
      </span>
    </div>
  );
}

function SteeringRow({
  signal,
  runway,
  drawIn,
  busy,
  onExtend,
  onDismiss,
  onGraduate,
  onOpenSources,
}: {
  signal: MarketSignal;
  runway: Runway;
  drawIn: boolean;
  busy: boolean;
  onExtend?: (validUntil: string | null) => void;
  onDismiss?: () => void;
  onGraduate?: () => void;
  onOpenSources: () => void;
}) {
  const [extending, setExtending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const canWrite = !!(onExtend || onDismiss || onGraduate);
  const titleId = `sig-steer-title-${signal.id}`;

  const daysClass = cn(
    "sig-steer__days",
    runway.openEnded || runway.expired
      ? "is-idle"
      : runway.state === "approaching"
        ? "is-approaching"
        : null,
  );

  return (
    <div
      className="sig-steer__row"
      id={`sig-${signal.id}`}
      role="group"
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      <div className="sig-steer__grid">
        <div className="sig-steer__main">
          <div className="sig-steer__meta">
            <KindGlyph kind={signal.kind} size={14} className="sig-glyph" />
            <span className="sig-kind-label">{kindLabelSingular(signal.kind)}</span>
            <span className="sig-dot" aria-hidden="true" />
            <SignalEvidenceSources signal={signal} onOpenSources={onOpenSources} />
          </div>
          <h3 className="sig-steer__title" id={titleId} dir="auto">
            {signal.title}
          </h3>
          <p className="sig-steer__body" dir="auto">
            {signal.body}
          </p>

          {canWrite ? (
            <div className="sig-steer__acts">
              {onExtend ? (
                <button
                  type="button"
                  className="sig-text-btn"
                  disabled={busy}
                  aria-describedby={titleId}
                  onClick={() => {
                    setConfirming(false);
                    setExtending(true);
                  }}
                  aria-expanded={extending}
                >
                  <CalendarPlus aria-hidden="true" />
                  Extend
                </button>
              ) : null}
              {onGraduate ? (
                <button
                  type="button"
                  className="sig-text-btn is-accent"
                  disabled={busy}
                  aria-describedby={titleId}
                  aria-label="Graduate to Brand DNA"
                  aria-expanded={confirming}
                  onClick={() => {
                    setExtending(false);
                    setConfirming(true);
                  }}
                >
                  <Sparkle aria-hidden="true" />
                  Graduate
                </button>
              ) : null}
              {onDismiss ? (
                <button
                  type="button"
                  className="sig-text-btn"
                  disabled={busy}
                  aria-describedby={titleId}
                  onClick={onDismiss}
                >
                  <X aria-hidden="true" />
                  Dismiss
                </button>
              ) : null}
          </div>
          ) : null}
        </div>

        <div className="sig-steer__run">
          <span className="sig-steer__today" aria-hidden="true" />
          <RunwayBar runway={runway} drawIn={drawIn} />
        </div>

        <div className={daysClass}>
          {runway.openEnded ? (
            <span className="sig-steer__days-n">Open</span>
          ) : runway.expired ? (
            <span className="sig-steer__days-n">Ended</span>
          ) : (
            <>
              <span className="sig-steer__days-n">{runway.days}</span>
              <span className="sig-steer__days-l">{runway.days === 1 ? "Day" : "Days"}</span>
            </>
          )}
        </div>
      </div>

      {/* Signals / Steering · Graduate: a strip under the row in the solid surface,
          the question over the signal's title, Cancel and Confirm at the end. */}
      {canWrite && confirming && onGraduate ? (
        <div className="sig-steer__strip sig-steer__strip--ask">
          <GraduationCap className="sig-steer__ask-icon" aria-hidden="true" />
          <span className="sig-steer__ask">
            <span className="sig-steer__ask-q">Promote to permanent Brand DNA?</span>
            <span className="sig-steer__ask-quote" dir="auto">
              “{signal.title}” stops running out and becomes a fact in the universe.
            </span>
          </span>
          <KitButton variant="ghost" isDisabled={busy} onClick={() => setConfirming(false)}>
            Cancel
          </KitButton>
          <KitButton isDisabled={busy} onClick={onGraduate}>
            Confirm
          </KitButton>
        </div>
      ) : null}

      {canWrite && extending && onExtend ? (
        <div className="sig-steer__strip sig-steer__picker">
          <LifetimePicker
            kind={signal.kind}
            recommendedValidUntil={signal.recommendedValidUntil}
            confirmLabel="Extend"
            isPending={busy}
            onConfirm={(validUntil) => {
              onExtend(validUntil);
              setExtending(false);
            }}
            onCancel={() => setExtending(false)}
          />
        </div>
      ) : null}
    </div>
  );
}

/** The share of a signal's own window already behind it, 0..1 (the phone row). */
function elapsedShare(signal: MarketSignal, now: Date): number {
  if (!signal.validUntil) return 1;
  const from = parseDate(signal.validFrom).getTime();
  const until = parseDate(signal.validUntil).getTime();
  if (until <= from) return 1;
  return Math.max(0, Math.min(1, (now.getTime() - from) / (until - from)));
}

/**
 * Signals / Steering row · Phone: one card per signal (radius 12, 14 in): the
 * kind and its sources in mono, the title with the days left beside it, the
 * window as a 4px bar of its own (elapsed in muted ink, the rest in the horizon
 * colour), then when it ends and the actions as words.
 */
function PhoneSteeringRow({
  signal,
  runway,
  now,
  busy,
  onExtend,
  onDismiss,
  onGraduate,
  onOpenSources,
}: {
  signal: MarketSignal;
  runway: Runway;
  now: Date;
  busy: boolean;
  onExtend?: (validUntil: string | null) => void;
  onDismiss?: () => void;
  onGraduate?: () => void;
  onOpenSources: () => void;
}) {
  const [extending, setExtending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const titleId = `sig-steer-title-${signal.id}`;
  const sources = signal.groundingObservations.length;
  const share = elapsedShare(signal, now);

  return (
    <div className="sig-psteer" id={`sig-${signal.id}`} role="group" aria-labelledby={titleId} tabIndex={-1}>
      <div className="sig-psteer__meta">
        <span>{kindLabelSingular(signal.kind)}</span>
        {sources > 0 ? (
          <>
            <span className="sig-dot" aria-hidden="true" />
            <button type="button" className="sig-psteer__sources" onClick={onOpenSources}>
              {sources} {sources === 1 ? "source" : "sources"}
            </button>
          </>
        ) : null}
      </div>
      <div className="sig-psteer__row">
        <h3 className="sig-psteer__title" id={titleId} dir="auto">
          {signal.title}
        </h3>
        <div
          className={cn(
            "sig-psteer__days",
            runway.openEnded || runway.expired ? "is-idle" : runway.state === "approaching" ? "is-approaching" : null,
          )}
        >
          {runway.openEnded ? (
            <span className="sig-psteer__count">Open</span>
          ) : runway.expired ? (
            <span className="sig-psteer__count">Ended</span>
          ) : (
            <>
              <span className="sig-psteer__count">{runway.days}</span>
              <span className="sig-psteer__unit">{runway.days === 1 ? "Day" : "Days"}</span>
            </>
          )}
        </div>
      </div>
      <div className="sig-psteer__run" role="img" aria-label={runway.aria}>
        <span className="sig-psteer__elapsed" style={{ flexGrow: share }} />
        {runway.expired ? null : (
          <span
            className="sig-psteer__left"
            style={{ flexGrow: 1 - share, background: HORIZON_STATE_COLORS[runway.state] }}
          />
        )}
      </div>
      {extending && onExtend ? (
        <LifetimePicker
          kind={signal.kind}
          recommendedValidUntil={signal.recommendedValidUntil}
          confirmLabel="Extend"
          isPending={busy}
          onConfirm={(validUntil) => {
            onExtend(validUntil);
            setExtending(false);
          }}
          onCancel={() => setExtending(false)}
        />
      ) : (
        <div className="sig-psteer__foot">
          <span className="sig-psteer__ends">{runway.endLabel}</span>
          {confirming && onGraduate ? (
            <>
              <button type="button" className="sig-text-btn" disabled={busy} onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button type="button" className="sig-text-btn is-accent" disabled={busy} onClick={onGraduate}>
                Graduate it
              </button>
            </>
          ) : (
            <>
              {onExtend ? (
                <button
                  type="button"
                  className="sig-text-btn is-accent"
                  disabled={busy}
                  aria-describedby={titleId}
                  onClick={() => setExtending(true)}
                >
                  Extend
                </button>
              ) : null}
              {onGraduate ? (
                <button
                  type="button"
                  className="sig-text-btn is-accent"
                  disabled={busy}
                  aria-describedby={titleId}
                  aria-label="Graduate to Brand DNA"
                  onClick={() => setConfirming(true)}
                >
                  Graduate
                </button>
              ) : null}
              {onDismiss ? (
                <button
                  type="button"
                  className="sig-text-btn"
                  disabled={busy}
                  aria-describedby={titleId}
                  onClick={onDismiss}
                >
                  Dismiss
                </button>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * STEERING: every signal in motion on one timeline sheet. Each row is the claim
 * on the left, its window as a runway across the last month and the next
 * quarter, and the days it has left on the right. One accent line marks today
 * down the whole sheet, so "which of these ends first" is read by eye before
 * any number is.
 *
 * The runways draw in once, the first time this sheet mounts in a visit
 * (`drawIn`), and never again: not on a tab switch back, not for a row that
 * arrives later.
 */
export default function SteeringSheet({
  signals,
  canEdit,
  isBusy,
  drawIn,
  onDrawn,
  onExtend,
  onDismiss,
  onGraduate,
  onOpenSources,
}: {
  /** Active (steering) signals; the sheet sorts them. */
  signals: MarketSignal[];
  canEdit: boolean;
  isBusy: (id: string) => boolean;
  drawIn: boolean;
  /** Called once the first draw has been mounted, so later mounts stay still. */
  onDrawn: () => void;
  onExtend: (signal: MarketSignal, validUntil: string | null) => void;
  onDismiss: (signal: MarketSignal) => void;
  onGraduate: (signal: MarketSignal) => void;
  onOpenSources: (signal: MarketSignal) => void;
}) {
  // One clock per render, so every row and the axis agree on "today".
  const [now] = useState(() => new Date());
  const scale = useMemo(() => makeScale(now), [now]);
  const ticks = useMemo(() => monthTicks(scale), [scale]);

  const rows = useMemo(
    () =>
      [...signals]
        .sort((a, b) => {
          const d = sortKey(a, now) - sortKey(b, now);
          if (d !== 0 && !Number.isNaN(d)) return d;
          return a.title.localeCompare(b.title);
        })
        .map((s) => ({ signal: s, runway: runwayOf(s, scale) })),
    [signals, now, scale],
  );

  useEffect(() => {
    if (drawIn) onDrawn();
  }, [drawIn, onDrawn]);

  const isPhone = useMediaQuery("(max-width: 767px)");
  if (isPhone) {
    return (
      <div className="sig-psteer-list">
        {rows.map(({ signal, runway }) => (
          <PhoneSteeringRow
            key={signal.id}
            signal={signal}
            runway={runway}
            now={now}
            busy={isBusy(signal.id)}
            onExtend={canEdit ? (validUntil) => onExtend(signal, validUntil) : undefined}
            onDismiss={canEdit ? () => onDismiss(signal) : undefined}
            onGraduate={canEdit ? () => onGraduate(signal) : undefined}
            onOpenSources={() => onOpenSources(signal)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="sig-steer" style={{ "--sig-today": `${TODAY_X}%` } as CSSProperties}>
      <div className="sig-steer__head" aria-hidden="true">
        <span className="sig-steer__head-sig">
          Signal <span className="sig-faint">· Ending soonest first</span>
        </span>
        <div className="sig-axis">
          {ticks.map((t) => (
            <span key={t.key} className="sig-axis__month" style={{ insetInlineStart: `${t.x}%` }}>
              {t.label}
            </span>
          ))}
          <span className="sig-axis__today">Today {formatShortDate(now.toISOString(), now)}</span>
        </div>
        <span className="sig-steer__head-days">Days left</span>
      </div>

      {rows.map(({ signal, runway }) => (
        <SteeringRow
          key={signal.id}
          signal={signal}
          runway={runway}
          drawIn={drawIn}
          busy={isBusy(signal.id)}
          onExtend={canEdit ? (validUntil) => onExtend(signal, validUntil) : undefined}
          onDismiss={canEdit ? () => onDismiss(signal) : undefined}
          onGraduate={canEdit ? () => onGraduate(signal) : undefined}
          onOpenSources={() => onOpenSources(signal)}
        />
      ))}
    </div>
  );
}
